import { Suspense, useRef, useState, useTransition } from 'react';
import type { Key } from 'react-aria-components';
import { Collection, Input, MenuTrigger, Text } from 'react-aria-components';
import { useInView } from 'react-intersection-observer';
import type { Path } from 'react-router';
import { useLocation } from 'react-router';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { projectSwitcherInternalGetEndpoint } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toProjects, toProjectSettings, toSwitchedProject } from '@gonfalon/navigator/navigators';
import { useRecentProjects } from '@gonfalon/recent-projects/useRecentProjects';
import {
  getInfiniteProjectsInternalQuery,
  getInfiniteProjectsQuery,
  getProjectQuery,
} from '@gonfalon/rest-api/queries/projects';
import { Autocomplete } from '@gonfalon/ui/Autocomplete';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import type { SearchFieldProps } from '@launchpad-ui/components';
import {
  Button,
  Group,
  Header,
  IconButton,
  LinkButton,
  Menu,
  MenuItem,
  MenuSection,
  Popover,
  ProgressBar,
  SearchField,
} from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';

import { isFlattenedSettingsEnabled } from '../SettingsNavigation/isFlattenedSettingsEnabled';
import {
  type ProjectSwitcherSource,
  trackProjectSwitcherOpened,
  trackProjectSwitcherProjectSelected,
  trackProjectSwitcherShowAllClicked,
  trackSettingsOpened,
} from '../SettingsNavigation/settingsAnalytics';

import { numberOfProjectItems } from './constants';

import styles from './ProjectSwitcher.module.css';

// If a project is keyed "settings", "switch", or "actions", its MenuItem id would collide with
// the MenuSection id, causing the item to be re-parented under the wrong section.
// Using a "section:" prefix ensures project keys (which don't contain colons) never collide.
const PROJECT_SETTINGS_ID = 'section:settings-item';

export function ProjectSwitcher() {
  const {
    project: { key: projectKey },
  } = useProjectContext();
  const {
    data: { name },
  } = useSuspenseQuery(getProjectQuery({ projectKey }));
  const { ref, props: overflowProps } = useTextOverflow<HTMLSpanElement>({});

  return (
    <MenuTrigger onOpenChange={trackOpened('sidebar')}>
      <Button className={styles.trigger} aria-label={msg('chrome.projectSwitcher.actionsLabel')}>
        <div className={styles.nameContainer}>
          <Icon name="folders" size="small" />
          <span ref={ref} {...overflowProps}>
            {name}
          </span>
        </div>
        <Icon name="chevron-down" size="small" />
      </Button>

      <Popover placement="bottom start" className={styles.popover}>
        <Suspense
          fallback={
            <Box width="$240" height="$208" display="grid" alignItems="center" justifyContent="center">
              <ProgressBar aria-label={msg('chrome.status.loading')} isIndeterminate size="small" />
            </Box>
          }
        >
          <ProjectSwitcherDialog source="sidebar" />
        </Suspense>
      </Popover>
    </MenuTrigger>
  );
}

export function ProjectSwitcherSkeleton() {
  return (
    <Button className={styles.trigger} isDisabled aria-label={msg('chrome.projectSwitcher.loadingProject')}>
      <span>
        <Message id="chrome.status.loading" />
      </span>
      <Icon name="chevron-down" size="small" />
    </Button>
  );
}

export function ProjectBreadcrumbSwitcher() {
  const {
    project: { key: projectKey },
  } = useProjectContext();
  const {
    data: { name },
  } = useSuspenseQuery(getProjectQuery({ projectKey }));

  return (
    <MenuTrigger onOpenChange={trackOpened('settings-breadcrumb')}>
      <Button className={styles.breadcrumbTrigger} aria-label={msg('chrome.projectSwitcher.switchLabel')}>
        <Icon name="folders" size="small" />
        {name}
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover placement="bottom start" className={styles.popover}>
        <Suspense
          fallback={
            <Box width="$240" height="$208" display="grid" alignItems="center" justifyContent="center">
              <ProgressBar aria-label={msg('chrome.status.loading')} isIndeterminate size="small" />
            </Box>
          }
        >
          <ProjectSwitcherDialog source="settings-breadcrumb" />
        </Suspense>
      </Popover>
    </MenuTrigger>
  );
}

function trackOpened(source: ProjectSwitcherSource) {
  return (isOpen: boolean) => {
    if (isOpen) {
      trackProjectSwitcherOpened({ source, fromPath: window.location.pathname });
    }
  };
}

function ProjectSwitcherDialog({ source }: { source: ProjectSwitcherSource }) {
  const location = useLocation();
  const { project: value } = useProjectContext();
  const { keys: recentProjectKeys, projects: recentProjects } = useRecentProjects();
  const [isBusy, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const offsetRef = useRef(0);
  const { data: projectsData, fetchNextPage } = useSuspenseInfiniteQuery(
    projectSwitcherInternalGetEndpoint()
      ? {
          ...getInfiniteProjectsInternalQuery({
            query: {
              limit: numberOfProjectItems,
              offset: 0,
              sort: 'name',
              filter: {
                query: filterText !== '' ? filterText : undefined,
              },
            },
          }),
          /**
           * The `/internal/projects` endpoint has a hardcoded totalCount of -1,
           * because accounts with thousands of projects can run into performance issues calculating the total count.
           * That makes the default `getNextPageParam` not work, because it relies on the presense of a `_links.next` link.
           * Our backend pagination code won't provide one if the `offset > totalCount`, which in this case will always be true.
           * So instead we use a ref to track the current offset, which is updated to be projects.length + numberOfProjectItems every time we fetch a new page,
           * and we override the default `getNextPageParam` to use this manually-tracked offset.
           */
          getNextPageParam: () => ({ offset: offsetRef.current }),
        }
      : getInfiniteProjectsQuery({
          query: {
            limit: numberOfProjectItems,
            offset: 0,
            sort: 'name',
            filter: {
              query: filterText !== '' ? filterText : undefined,
            },
          },
        }),
  );

  const projects = projectsData?.pages?.map((page) => page?.items)?.flat() ?? [];

  const { ref } = useInView({
    onChange(inView) {
      if (inView) {
        if (projectSwitcherInternalGetEndpoint()) {
          offsetRef.current = projects.length + numberOfProjectItems;
        }
        fetchNextPage().catch(() => {});
      }
    },
  });

  type Item =
    | { type: 'recent'; id: Key; name: string; href: Path }
    | {
        type: 'project';
        id: Key;
        name: string;
        href: Path;
      }
    | { type: 'setting'; id: Key; name: string; icon: IconName; href: Path };

  const projectItems: Item[] = [];

  for (const recent of recentProjects) {
    if (filterText) {
      continue;
    }

    if (recent.key === value.key && (filterText === undefined || filterText === '')) {
      continue;
    }

    projectItems.push({
      type: 'recent',
      id: recent.key,
      name: recent.name,
      href: toSwitchedProject({ projectKey: recent.key, pathname: location.pathname }, { search: location.search }),
    });
  }

  for (const project of projects) {
    // Don't include current project if we're not filtering
    if (project.key === value.key && (filterText === undefined || filterText === '')) {
      continue;
    }

    // Skip project if it was recently seen and there is no filter
    if (recentProjectKeys.has(project.key) && (filterText === undefined || filterText === '')) {
      continue;
    }

    projectItems.push({
      type: 'project',
      id: project.key,
      name: project.name,
      href: toSwitchedProject({ projectKey: project.key, pathname: location.pathname }, { search: location.search }),
    });
  }

  type Section = {
    id: 'section:settings' | 'section:actions' | 'section:switch';
    name?: string;
    children: Item[];
  };

  const items: Section[] = [];

  const onShowAll = () => trackProjectSwitcherShowAllClicked({ source, fromPath: location.pathname });
  const onSelect = (item: Item) => {
    if (item.type === 'setting') {
      trackSettingsOpened({
        entry: 'project-switcher',
        target: 'project-settings',
        fromPath: location.pathname,
        flattened: false,
      });
      return;
    }
    trackProjectSwitcherProjectSelected({
      source,
      itemType: item.type,
      searched: filterText !== '',
      queryLength: filterText.length,
      position: projectItems.indexOf(item) + 1,
      fromPath: location.pathname,
    });
  };

  if (projectItems.length > 0) {
    items.push({ id: 'section:switch', name: 'Switch project', children: projectItems } as const);
  }

  if (!isFlattenedSettingsEnabled()) {
    items.push({
      id: 'section:settings',
      name: value.name,
      children: [
        {
          type: 'setting',
          id: PROJECT_SETTINGS_ID,
          name: 'Project settings',
          icon: 'gear',
          href: toProjectSettings({ projectKey: value.key }),
        },
      ],
    } as const);
  }

  return (
    <Autocomplete>
      <ProjectSearchField
        autoFocus
        className={styles.search}
        isBusy={isBusy}
        onChange={(text) =>
          startTransition(() => {
            setFilterText(text);
          })
        }
      />
      <Menu
        aria-label={msg('chrome.projectSwitcher.dialogLabel')}
        items={items}
        renderEmptyState={() =>
          isFlattenedSettingsEnabled() && !filterText ? (
            <LinkButton
              className={styles.viewAll}
              variant="minimal"
              size="small"
              href={toProjects()}
              onPress={onShowAll}
            >
              {msg('chrome.projectSwitcher.viewAll')}
            </LinkButton>
          ) : (
            'No projects found'
          )
        }
      >
        {(section) => (
          <MenuSection id={section.id} className={styles.section}>
            {'name' in section ? (
              <Header>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  {section.name}
                  {section.id === 'section:switch' && (
                    <LinkButton
                      className={styles.viewAll}
                      variant="minimal"
                      size="small"
                      href={toProjects()}
                      onPress={onShowAll}
                    >
                      {msg('chrome.projectSwitcher.viewAll')}
                    </LinkButton>
                  )}
                </Box>
              </Header>
            ) : null}
            <Collection items={section.children}>
              {(item) => {
                const isLastChild = item.id === section.children[section.children.length - 1].id;
                return (
                  <MenuItem
                    id={item.id}
                    key={item.id}
                    textValue={item.name}
                    href={item.href}
                    onAction={() => onSelect(item)}
                  >
                    <Text slot="label" ref={isLastChild ? ref : undefined} className={styles.menuItem}>
                      {'icon' in item ? <Icon name={item.icon} size="small" /> : null}
                      <TextTruncator>{item.name}</TextTruncator>
                    </Text>
                  </MenuItem>
                );
              }}
            </Collection>
          </MenuSection>
        )}
      </Menu>
    </Autocomplete>
  );
}

interface ProjectSearchFieldProps extends SearchFieldProps {
  isBusy: boolean;
}

export function ProjectSearchField({ isBusy, ...props }: ProjectSearchFieldProps) {
  return (
    <SearchField {...props} aria-label={msg('chrome.projectSwitcher.searchPlaceholder')} autoFocus>
      <Group>
        <Icon name="search" size="small" />
        <Input placeholder={msg('chrome.projectSwitcher.searchPlaceholder')} />
        {isBusy && (
          <span>
            <ProgressBar aria-label={msg('chrome.status.loading')} isIndeterminate size="small" />
          </span>
        )}
        <IconButton aria-label="clear" icon="cancel-circle-outline" size="small" variant="minimal" />
      </Group>
    </SearchField>
  );
}
